import React, { useContext, useEffect } from "react";
import moment from "moment-timezone";
import { DefaultSeo } from "next-seo";
import { useReactiveVar } from "@apollo/client";
import { isMobile } from "react-device-detect";
import { useRouter } from "next/router";

// Components
import NavBar from "src/components/NavBar";
import Main from "src/components/Main";
import Footer from "src/components/Footer";
import LoginModal from "src/components/LoginModal";
import PricingModal from "src/components/Pricing/PricingModal";
import MarketingCampaignModal from "src/components/MarketingCampaignModal";
import StateCaptureModal from "src/components/StateCaptureModal";
import SportMismatchModal from "src/components/SportMismatchModal";
import FreeTrialBanner from "src/components/FreeTrialBanner";
import CollectSportsbookModal from "src/components/CollectSportsbookModal";
import SkipToContent from "src/components/SkipToContent";
import CustomOfferModal from "src/components/CustomOfferModal";
import PromotionBanner from "src/components/PromotionBanner";
import LeaguePromoBanner from "src/components/LeaguePromoBanner";
import PlayerCardModal from "src/components/PlayerCard/PlayerCardModal";
// Services
import { SubscriptionContext, getActiveLeaguePromo } from "src/services/SubscriptionService";
import { customOfferModalOpen, isHeadless, resolveHeadlessMode } from "src/services/ApolloService";
import AdZoneService, { AdZoneEnum } from "src/services/AdZoneService";
// Misc
import SEO from "src/next-seo.config";
import { DEFAULT_TIMEZONE, PLAYER_CARDS_ENABLED } from "src/constants";
import { isNcaaTournamentInProgress } from "src/services/ScheduleService";
import styles from "src/styles/global.scss";
import AdaptiveOddsserveAd from "./AdaptiveOddsserveAd";

const todaysDate = moment.tz(DEFAULT_TIMEZONE);

export interface PageProps {
  children: React.ReactNode;
}

const Page = ({ children }: PageProps) => {
  const ctx = useContext(SubscriptionContext);
  const router = useRouter();
  const headless = useReactiveVar(isHeadless);
  const customOfferModalShow = useReactiveVar(customOfferModalOpen);
  const shellMode = resolveHeadlessMode(router.pathname, router.asPath, router.query?.headless) || headless;
  const hideShell = Boolean(shellMode);
  const leaguePromo = ctx.subscriptionLevel ? undefined : getActiveLeaguePromo(ctx.leaguePromos);
  // we use these as key so they need to be lowercase
  // vercel will match upper or lower case patterns
  let pageKey: string | null = null;
  if (typeof router.query?.pageKey === "string") {
    pageKey = router.query?.pageKey?.toLowerCase();
  }

  const noIndexObject =
    process.env.NEXT_PUBLIC_NOINDEX === "true"
      ? {
          dangerouslySetAllPagesToNoIndex: true,
          dangerouslySetAllPagesToNoFollow: true,
        }
      : {};

  useEffect(() => {
    const { gclid, couponId } = router.query;
    const betqlgclid: string = gclid as any;

    if (betqlgclid) {
      window.localStorage.setItem("betqlgclid", betqlgclid);
    }

    if (couponId && isMobile) {
      router.push({ pathname: "/subscribe", query: { couponId: couponId as string } });
    }
  }, []);
  return (
    <React.Fragment>
      {/* TODO: the sport check is a one off, lets remove after promo */}
      {!hideShell &&
      !ctx.hasSubscriptionAccess("CFB") &&
      todaysDate.isBetween(
        AdZoneService.getStartDate(AdZoneEnum.SITE_TOP_BANNER),
        AdZoneService.getEndDate(AdZoneEnum.SITE_TOP_BANNER)
      ) ? (
        <button
          className="site-banner"
          onClick={() => router.push({ pathname: "/subscribe", query: { source: "Bowl Season Deal" } })}
        >
          {AdZoneService.getBannerText(AdZoneEnum.CHECKOUT_MODAL_BANNER)}
        </button>
      ) : null}
      <div className="d-flex flex-column h-100 w-100" style={{ position: "relative" }}>
        <style jsx global>
          {styles}
        </style>
        <DefaultSeo {...SEO} {...noIndexObject} />
        {!hideShell && <SkipToContent />}
        <LoginModal />
        <PricingModal />
        <MarketingCampaignModal pageKey={pageKey} />
        <StateCaptureModal />
        <SportMismatchModal />
        <CollectSportsbookModal />
        {customOfferModalShow && <CustomOfferModal />}
        {/* FreeTrialBanner slots in above the NavBar */}
        {!hideShell && (
          <>
            {isNcaaTournamentInProgress() ? (
              <PromotionBanner />
            ) : leaguePromo ? (
              <LeaguePromoBanner promo={leaguePromo} />
            ) : (
              <FreeTrialBanner />
            )}
            <div className="top-banner">
              <AdaptiveOddsserveAd
                desktopPlacementId="f76f80e495c757de531314069b7c01de"
                mobilePlacementId="11cb1d470e7028030bbbe5d5e789a39c"
              />
            </div>
            <NavBar />
          </>
        )}
        {PLAYER_CARDS_ENABLED ? (
          <PlayerCardModal>
            <Main>{children}</Main>
          </PlayerCardModal>
        ) : (
          <Main>{children}</Main>
        )}
        {hideShell ? shellMode === "funnel" && <Footer bottomOnly /> : <Footer />}
      </div>
    </React.Fragment>
  );
};

export default Page;
